import { useMemo, useState } from "react";
import Dialog from "./Dialog";
import Icon from "./Icon";
import { formatPrice } from "../../menu.js";
import { ROUNDINGS, changeProblem, deltaLabel, planPriceChange, productsInScope } from "../../bulkPrice.js";
import "../../panel-import.css";
import "../../panel-prices.css";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const PREVIEW = 40;
const QUICK_PERCENTS = ["5", "10", "15", "20"];

const Choice = ({ on, onClick, title, text, disabled }) => (
    <button type="button" role="radio" aria-checked={on} className={on ? "lp-imp-mode is-on" : "lp-imp-mode"} onClick={onClick} disabled={disabled}>
        <span className="lp-imp-radio" aria-hidden="true"></span>
        <strong>{title}</strong>
        <span className="lp-panel-muted">{text}</span>
    </button>
);

const Toggle = ({ label, value, options, onChange }) => (
    <div className="lp-bp-seg" role="radiogroup" aria-label={label}>
        {options.map(([id, text]) => (
            <button key={id} type="button" role="radio" aria-checked={value === id} className={value === id ? "is-on" : ""} onClick={() => onChange(id)}>{text}</button>
        ))}
    </div>
);

// Lista con casillas para elegir a mano qué productos cambian.
const PickProducts = ({ products, categories, selected, setSelected }) => {
    const [q, setQ] = useState("");
    const term = q.trim().toLowerCase();
    const list = products
        .filter((p) => !term || p.name?.toLowerCase().includes(term) || p.category?.toLowerCase().includes(term))
        .sort((a, b) => categories.indexOf(a.category) - categories.indexOf(b.category) || a.name.localeCompare(b.name, "es", { numeric: true }));
    const allOn = list.length > 0 && list.every((p) => selected.has(p.id));
    const toggle = (id) => setSelected((s) => {
        const next = new Set(s);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleAll = () => setSelected((s) => {
        const next = new Set(s);
        list.forEach((p) => allOn ? next.delete(p.id) : next.add(p.id));
        return next;
    });

  return (
    <div className="lp-bp-pick">
        <div className="lp-panel-search">
            <label htmlFor="bp-buscar" className="lp-panel-sr">Buscar producto</label>
            <Icon name="search" size={18}></Icon>
            <input id="bp-buscar" type="search" placeholder="Buscar producto o categoría…" value={q} onChange={(e) => setQ(e.target.value)}/>
        </div>
        <div className="lp-bp-pick-head">
            <span className="lp-panel-muted">{plural(selected.size, "elegido", "elegidos")}</span>
            {list.length > 0 && <button type="button" className="lp-text-link" onClick={toggleAll}>{allOn ? "Sacar todos" : term ? "Elegir los que se ven" : "Elegir todos"}</button>}
        </div>
        <ul className="lp-bp-pick-list">
            {list.map((p) => (
                <li key={p.id}>
                    <label className="lp-imp-check">
                        <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}/>
                        <span className="lp-bp-pick-name">{p.name}<span className="lp-panel-muted">{p.category}</span></span>
                        <span className="lp-bp-pick-price">{formatPrice(p.price)}</span>
                    </label>
                </li>
            ))}
            {!list.length && <li className="lp-panel-muted">No hay productos que coincidan.</li>}
        </ul>
    </div>
  )
}

const statusText = {
    same: "Queda igual",
    invalid: "El precio no es un número",
    tooLow: "Quedaría en $0 o menos",
    tooHigh: "Quedaría demasiado alto"
};

const Preview = ({ plan, problem, scoped }) => {
    const [all, setAll] = useState(false);
    // Primero los que cambian, después el resto.
    const rows = [...plan.rows].sort((a, b) => (a.status === "change" ? 0 : 1) - (b.status === "change" ? 0 : 1));
    const shown = all ? rows : rows.slice(0, PREVIEW);
    if (!scoped.length) return <p className="lp-panel-empty">Elegí al menos un producto para ver cómo quedan los precios.</p>;
  return (
    <>
        {!problem &&
            <div className="lp-imp-pills" aria-live="polite">
                <span className="lp-imp-pill is-listos">{plural(plan.update.length, "cambia", "cambian")}</span>
                {plan.same > 0 && <span className="lp-imp-pill is-todos">{plural(plan.same, "queda igual", "quedan igual")}</span>}
                {plan.skipped > 0 && <span className="lp-imp-pill is-errores">{plural(plan.skipped, "no se puede cambiar", "no se pueden cambiar")}</span>}
            </div>
        }
        <ul className="lp-bp-rows" aria-label="Vista previa de los precios">
            {shown.map(({ product, before, after, status }) => (
                <li key={product.id} className={"lp-bp-row is-" + status}>
                    <span className="lp-bp-row-name">{product.name}<span className="lp-panel-muted">{product.category}</span></span>
                    <span className="lp-bp-row-prices">
                        {status === "change" ?
                            <>
                                <s className="lp-panel-muted">{formatPrice(before)}</s>
                                <Icon name="arrow" size={14} strokeWidth={2.4}></Icon>
                                <strong>{formatPrice(after)}</strong>
                                <span className={after > before ? "lp-bp-delta is-up" : "lp-bp-delta is-down"}>{deltaLabel(before, after)}</span>
                            </> :
                            <>
                                <span>{before === null ? formatPrice(product.price) : formatPrice(before)}</span>
                                {status !== "pending" && <span className={status === "same" ? "lp-bp-delta" : "lp-bp-delta is-bad"}>{statusText[status]}</span>}
                            </>
                        }
                    </span>
                </li>
            ))}
        </ul>
        {rows.length > PREVIEW && <button type="button" className="lp-text-link lp-imp-center" onClick={() => setAll(!all)}>{all ? "Ver menos" : `Ver los ${rows.length}`}</button>}
    </>
  )
}

// Cambiar muchos precios de una (plan Pro). Ocupa la pantalla de Productos mientras dura.
const BulkPricePanel = ({ data, onClose, initialCategory }) => {
    const { products, categories } = data;
    const usedCategories = categories.filter((c) => products.some((p) => p.category === c));

    const [scope, setScope] = useState(initialCategory ? "category" : "all");
    const [category, setCategory] = useState(initialCategory || usedCategories[0] || "");
    const [selected, setSelected] = useState(() => new Set());
    const [direction, setDirection] = useState("up");
    const [kind, setKind] = useState("percent");
    const [amount, setAmount] = useState("");
    const [rounding, setRounding] = useState(0);
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);

    const change = { kind, direction, amount, rounding };
    const problem = changeProblem(change);
    const scoped = useMemo(() => productsInScope(products, { scope, category, selected }), [products, scope, category, selected]);
    const plan = useMemo(() => planPriceChange(scoped, change), [scoped, kind, direction, amount, rounding]); // eslint-disable-line react-hooks/exhaustive-deps
    const count = plan.update.length;

    const verb = direction === "up" ? "Subir" : "Bajar";
    const howMuch = kind === "percent" ? `${amount.trim().replace(".", ",")}%` : formatPrice(Number(amount.trim().replace(",", ".")));
    const where = scope === "all" ? "todo el catálogo" : scope === "category" ? category : plural(selected.size, "producto elegido", "productos elegidos");
    const description = `${verb} ${howMuch} · ${where}${rounding ? ` · redondeo a $${rounding}` : ""}`;

    const apply = async () => {
        setBusy(true);
        const ok = await data.changePrices(plan.update, description);
        setBusy(false);
        if (ok) {
            setConfirming(false);
            onClose();
        }
    }

  return (
    <div className="lp-panel-page lp-imp lp-bp">
        <button type="button" className="lp-imp-back" onClick={onClose}><Icon name="arrow" size={16} strokeWidth={2.4}></Icon> Volver a Productos</button>
        <div className="lp-panel-head">
            <div>
                <h1>Cambiar precios</h1>
                <p className="lp-panel-muted">Subí o bajá muchos precios de una vez. Antes de guardar ves cómo queda cada uno.</p>
            </div>
        </div>

        <div className="lp-bp-layout">
            <section className="lp-panel-card lp-panel-stack lp-imp-gap">
                <h2 className="lp-imp-h3">1. ¿A qué productos?</h2>
                <div className="lp-imp-modes" role="radiogroup" aria-label="A qué productos">
                    <Choice on={scope === "all"} onClick={() => setScope("all")} title="Todo el catálogo" text={plural(products.length, "producto", "productos")}></Choice>
                    <Choice on={scope === "category"} onClick={() => setScope("category")} disabled={!usedCategories.length} title="Una categoría" text="Por ejemplo, solo las pizzas o solo las bebidas."></Choice>
                    <Choice on={scope === "selected"} onClick={() => setScope("selected")} title="Elegir productos" text="Marcás uno por uno los que querés cambiar."></Choice>
                </div>
                {scope === "category" &&
                    <div className="lp-field">
                        <label htmlFor="bp-categoria">Categoría</label>
                        <select id="bp-categoria" value={category} onChange={(e) => setCategory(e.target.value)}>
                            {usedCategories.map((c) => <option key={c} value={c}>{c} ({products.filter((p) => p.category === c).length})</option>)}
                        </select>
                    </div>
                }
                {scope === "selected" && <PickProducts products={products} categories={categories} selected={selected} setSelected={setSelected}></PickProducts>}

                <h2 className="lp-imp-h3">2. ¿Cuánto?</h2>
                <div className="lp-bp-row-ctrls">
                    <Toggle label="Subir o bajar" value={direction} onChange={setDirection} options={[["up", "Subir"], ["down", "Bajar"]]}></Toggle>
                    <Toggle label="Porcentaje o monto fijo" value={kind} onChange={setKind} options={[["percent", "Porcentaje"], ["fixed", "Monto fijo"]]}></Toggle>
                </div>
                <div className="lp-field">
                    <label htmlFor="bp-monto">{kind === "percent" ? `Porcentaje a ${direction === "up" ? "subir" : "bajar"}` : `Monto a ${direction === "up" ? "sumar" : "restar"} a cada precio`}</label>
                    <div className={"lp-bp-amount" + (kind === "percent" ? " is-percent" : "")}>
                        {kind === "fixed" && <span aria-hidden="true">$</span>}
                        <input id="bp-monto" type="text" inputMode="decimal" autoComplete="off" placeholder={kind === "percent" ? "10" : "500"} value={amount}
                            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ""))}
                            aria-invalid={!!amount && !!problem} aria-describedby="bp-monto-msg"/>
                        {kind === "percent" && <span aria-hidden="true">%</span>}
                    </div>
                    {kind === "percent" &&
                        <div className="lp-panel-chips lp-bp-quick">
                            {QUICK_PERCENTS.map((n) => <button key={n} type="button" className={amount === n ? "lp-panel-chip lp-panel-chip-on" : "lp-panel-chip"} onClick={() => setAmount(n)}>{n}%</button>)}
                        </div>
                    }
                    <span id="bp-monto-msg" className={amount && problem ? "lp-imp-msg is-err" : "lp-panel-sr"}>{amount && problem}</span>
                </div>

                <h2 className="lp-imp-h3">3. ¿Redondeamos?</h2>
                <div className="lp-panel-chips" role="radiogroup" aria-label="Redondeo">
                    {ROUNDINGS.map((r) => (
                        <button key={r} type="button" role="radio" aria-checked={rounding === r} className={rounding === r ? "lp-panel-chip lp-panel-chip-on" : "lp-panel-chip"} onClick={() => setRounding(r)}>
                            {r ? `A $${r}` : "Sin redondear"}
                        </button>
                    ))}
                </div>
                <p className="lp-panel-muted lp-bp-hint">{rounding ? `Cada precio queda en el múltiplo de $${rounding} más cercano. Por ejemplo, $1.265 pasa a ${formatPrice(Math.round(1265 / rounding) * rounding)}.` : "Los precios quedan con la cuenta exacta, redondeada al peso."}</p>
            </section>

            <section className="lp-panel-card lp-panel-stack lp-imp-gap lp-bp-preview">
                <h2 className="lp-imp-h3">Así quedan</h2>
                <Preview key={scope + category} plan={plan} problem={problem} scoped={scoped}></Preview>
                <p className="lp-imp-note is-info"><Icon name="refresh" size={18} strokeWidth={2.2}></Icon><span>Si te equivocás, durante 24 horas podés <b>deshacer el cambio</b> desde Productos.</span></p>
                <div className="lp-imp-footer">
                    <button type="button" className="lp-btn lp-btn-secondary" onClick={onClose}>Cancelar</button>
                    <button type="button" className="lp-btn lp-btn-primary" disabled={!!problem || !count} onClick={() => setConfirming(true)}>
                        <Icon name="check" size={18} strokeWidth={2.4}></Icon> {count ? `Cambiar ${plural(count, "precio", "precios")}` : "Cambiar precios"}
                    </button>
                </div>
            </section>
        </div>

        <Dialog open={confirming} title="¿Cambiamos los precios?" onClose={() => !busy && setConfirming(false)}>
            <p className="lp-panel-muted">Vas a cambiar <strong>{plural(count, "precio", "precios")}</strong> ({description}). Tus clientes los ven en el menú apenas confirmes.</p>
            <div className="lp-panel-actions">
                <button type="button" className="lp-btn lp-btn-primary" disabled={busy} onClick={apply}>{busy ? "Guardando..." : "Sí, cambiarlos"}</button>
                <button type="button" className="lp-btn lp-btn-secondary" disabled={busy} onClick={() => setConfirming(false)}>Cancelar</button>
            </div>
        </Dialog>
    </div>
  )
}

export default BulkPricePanel
